/**
 * Portal reports list — one page per `kind` (Incidents / Maintenance / DARs /
 * Tour Scans). Card layout mirrors the main-app pages exactly (Incidents.jsx,
 * DAR.jsx). Read-only: no Update / Delete / Summarize buttons — replaced by a
 * single "Details" button that opens the PortalReportDetail modal with all
 * fields + photo gallery.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errorMessage, API_BASE } from "../lib/api";
import { PageHeader, GhostButton, PillBadge } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import PortalReportDetail from "../components/PortalReportDetail";
import AttachmentViewer from "../components/AttachmentViewer";
import {
  AlertOctagon, Wrench, FileSignature, Route as RouteIcon,
  Image as ImageIcon, Sparkles, Download, Eye, X as XIcon,
  Layers,
} from "lucide-react";

// ─── Kind config ─── one shared page + a per-kind config object.
const KIND_CONFIG = {
  incidents: {
    title: "Incidents",
    subtitle: "Every incident our officers observed and reported across your sites — with photos, narrative, and Claude AI analysis when available.",
    icon: AlertOctagon,
    iconTone: "#FF3B30",
    empty: "No incidents in this window. All clear.",
    endpoint: "/portal/incidents",
    pdfPath: "incidents",
  },
  maintenance: {
    title: "Maintenance Requests",
    subtitle: "Maintenance issues our officers flagged at your sites during patrols — burnt lights, broken locks, leaks, damage — with priority and photos.",
    icon: Wrench,
    iconTone: "#FF9F0A",
    empty: "No maintenance requests in this window.",
    endpoint: "/portal/maintenance",
    pdfPath: "maintenance",
  },
  dar: {
    title: "Daily Activity Reports",
    subtitle: "End-of-shift narratives submitted by our officers — visitors and vehicles logged, equipment inspected, notable events, and photos.",
    icon: FileSignature,
    iconTone: "#0A84FF",
    empty: "No DARs in this window.",
    endpoint: "/portal/dar",
    pdfPath: "dar",
  },
  tour_scans: {
    title: "Tour Scans",
    subtitle: "Proof-of-patrol — every GPS and NFC checkpoint our officers hit during tours at your sites.",
    icon: RouteIcon,
    iconTone: "#22C55E",
    empty: "No tour scans in this window.",
    endpoint: "/portal/tour-scans",
    pdfPath: "tour_scans",
  },
};

const WINDOW_OPTIONS = [
  { days: 7,  label: "1 week" },
  { days: 14, label: "2 weeks" },
  { days: 30, label: "1 month" },
];


// ─── Card renderers — matched 1:1 to /app/frontend/src/pages/Incidents.jsx
// and DAR.jsx card layouts. Only the right-rail actions differ. ────────
function IncidentCard({ row, onDetails, onViewPhotos }) {
  const sevTone = ({ critical: "red", high: "red", medium: "amber", low: "blue" })[row.severity] || "default";
  const statTone = ({ open: "red", investigating: "amber", resolved: "green" })[row.status] || "default";
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors">
      <div className="flex items-start gap-4">
        <div className="size-10 shrink-0 border border-[#27272A] flex items-center justify-center">
          <AlertOctagon className="size-5 text-[#FF3B30]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl uppercase font-bold tracking-tight">{row.title}</h3>
            <PillBadge tone={sevTone}>{row.severity}</PillBadge>
            <PillBadge tone={statTone}>{row.status}</PillBadge>
            {row.incident_type && <PillBadge>{String(row.incident_type).replace("_", " ")}</PillBadge>}
            {row.injuries && <PillBadge tone="red">injuries</PillBadge>}
            {row.police_called && <PillBadge tone="amber">police</PillBadge>}
          </div>
          <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line line-clamp-3">{row.description}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
            <span>SITE: {row.site_name}</span>
            {row.guard_name && <span>OFFICER: {row.guard_name}</span>}
            {row.location_on_site && <span>LOC: {row.location_on_site}</span>}
            {row.occurred_at && <span>OCCURRED: {new Date(row.occurred_at).toLocaleString()}</span>}
            <span>LOGGED: {new Date(row.created_at).toLocaleString()}</span>
            {row.reported_by_name && <span>FILED BY: {row.reported_by_name}</span>}
          </div>
          {(row.photo_attachments || []).length > 0 && (
            <button
              type="button"
              data-testid={`portal-view-photos-${row.id}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0A84FF] hover:text-white"
              onClick={() => onViewPhotos(row)}
            >
              <ImageIcon className="size-3.5" /> {row.photo_attachments.length} photo{row.photo_attachments.length === 1 ? "" : "s"}
            </button>
          )}
          {row.ai_summary && (
            <div className="mt-4 border-l-2 border-[#FF3B30] bg-[#0A0A0A] p-3">
              <div className="label-mute flex items-center gap-2 mb-2"><Sparkles className="size-3" /> AI Analysis (Claude)</div>
              <div className="text-sm whitespace-pre-line text-zinc-300 line-clamp-4">{row.ai_summary}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <GhostButton
            onClick={() => onDetails(row)}
            data-testid={`portal-report-details-${row.id}`}
          >
            <Eye className="size-3.5" /> Details
          </GhostButton>
        </div>
      </div>
    </div>
  );
}

function MaintenanceCard({ row, onDetails, onViewPhotos }) {
  const prTone = ({ urgent: "red", high: "red", medium: "amber", low: "blue" })[row.priority] || "default";
  const statTone = ({ open: "red", in_progress: "amber", resolved: "green", closed: "green" })[row.status] || "default";
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors">
      <div className="flex items-start gap-4">
        <div className="size-10 shrink-0 border border-[#27272A] flex items-center justify-center">
          <Wrench className="size-5 text-[#FF9F0A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl uppercase font-bold tracking-tight">{row.title || row.subject || "Maintenance request"}</h3>
            {row.priority && <PillBadge tone={prTone}>{row.priority}</PillBadge>}
            {row.status && <PillBadge tone={statTone}>{String(row.status).replace("_", " ")}</PillBadge>}
            {row.category && <PillBadge>{row.category}</PillBadge>}
          </div>
          <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line line-clamp-3">{row.description}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
            <span>SITE: {row.site_name}</span>
            <span>LOGGED: {new Date(row.created_at).toLocaleString()}</span>
            {row.reported_by_name && <span>FILED BY: {row.reported_by_name}</span>}
          </div>
          {(row.photo_attachments || []).length > 0 && (
            <button
              type="button"
              data-testid={`portal-view-photos-${row.id}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0A84FF] hover:text-white"
              onClick={() => onViewPhotos(row)}
            >
              <ImageIcon className="size-3.5" /> {row.photo_attachments.length} photo{row.photo_attachments.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <GhostButton onClick={() => onDetails(row)} data-testid={`portal-report-details-${row.id}`}>
            <Eye className="size-3.5" /> Details
          </GhostButton>
        </div>
      </div>
    </div>
  );
}

function DarCard({ row, onDetails, onViewPhotos }) {
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5">
      <div className="flex items-start gap-4">
        <div className="size-10 shrink-0 border border-[#27272A] flex items-center justify-center">
          <FileSignature className="size-5 text-[#0A84FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl uppercase font-bold tracking-tight">{row.site_name || "Unknown site"}</h3>
            {row.client_name && <PillBadge>{row.client_name}</PillBadge>}
            {row.guard_name && <PillBadge tone="blue">{row.guard_name}</PillBadge>}
          </div>
          <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line line-clamp-3">{row.narrative}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
            <span>VISITORS: {row.visitor_count ?? 0}</span>
            <span>VEHICLES: {row.vehicle_count ?? 0}</span>
            {row.weather_conditions && <span>WEATHER: {row.weather_conditions}</span>}
            <span>SUBMITTED: {new Date(row.created_at).toLocaleString()}</span>
          </div>
          {(row.equipment_inspected || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {row.equipment_inspected.map((e) => <PillBadge key={e}>{e}</PillBadge>)}
            </div>
          )}
          {(row.photo_attachments || []).length > 0 && (
            <button
              type="button"
              data-testid={`portal-view-photos-${row.id}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0A84FF] hover:text-white"
              onClick={() => onViewPhotos(row)}
            >
              <ImageIcon className="size-3.5" /> {row.photo_attachments.length} photo{row.photo_attachments.length === 1 ? "" : "s"}
            </button>
          )}
          {row.ai_summary && (
            <div
              className="mt-4 border-l-2 border-[#0A84FF] bg-[#0A0A0A] p-3"
              data-testid={`portal-dar-ai-summary-${row.id}`}
            >
              <div className="label-mute flex items-center gap-2 mb-2"><Sparkles className="size-3" /> AI Summary (Claude)</div>
              <div className="text-sm whitespace-pre-line text-zinc-300 line-clamp-4">{row.ai_summary}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <GhostButton onClick={() => onDetails(row)} data-testid={`portal-report-details-${row.id}`}>
            <Eye className="size-3.5" /> Details
          </GhostButton>
        </div>
      </div>
    </div>
  );
}

function TourScanCard({ row, onDetails }) {
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors">
      <div className="flex items-start gap-4">
        <div className="size-10 shrink-0 border border-[#27272A] flex items-center justify-center">
          <RouteIcon className="size-5 text-[#22C55E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg uppercase font-bold tracking-tight">{row.stop_name || "Checkpoint"}</h3>
            <PillBadge tone="blue">{(row.method || "").toUpperCase()}</PillBadge>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
            <span>SITE: {row.site_name}</span>
            <span>OFFICER: {row.guard_name}</span>
            <span>AT: {new Date(row.captured_at || row.created_at).toLocaleString()}</span>
            {row.lat != null && row.lng != null && (
              <span>GPS: {Number(row.lat).toFixed(5)}, {Number(row.lng).toFixed(5)}</span>
            )}
          </div>
          {row.notes && (
            <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line line-clamp-2">{row.notes}</div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <GhostButton onClick={() => onDetails(row)} data-testid={`portal-report-details-${row.id}`}>
            <Eye className="size-3.5" /> Details
          </GhostButton>
        </div>
      </div>
    </div>
  );
}

const CARDS = {
  incidents: IncidentCard,
  maintenance: MaintenanceCard,
  dar: DarCard,
  tour_scans: TourScanCard,
};


// ─── Shared photo viewer (same behaviour as main app's DAR / Incidents) ─
function PhotoViewer({ items, title, onClose }) {
  if (!items) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="portal-photo-viewer">
      <div className="border border-[#27272A] bg-[#121212] w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#27272A] flex items-center justify-between">
          <div>
            <div className="label-mute">Attachments</div>
            <h3 className="font-display text-2xl uppercase font-bold tracking-tight">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:text-[#FF3B30] transition-colors">✕</button>
        </div>
        <div className="p-6">
          <AttachmentViewer items={items} testIdPrefix="portal-attachment" />
        </div>
      </div>
    </div>
  );
}


// ─── Main page ─────────────────────────────────────────────────────
export default function PortalReportsPage({ kind }) {
  const cfg = KIND_CONFIG[kind];
  const [searchParams, setSearchParams] = useSearchParams();

  // Portfolio drill-down: when the client-portal dashboard sent us here
  // via "?portfolio=<id>&portfolio_name=<name>" (or "unassigned"),
  // narrow the site filter to the sites in that portfolio.
  const portfolioFilter = searchParams.get("portfolio");
  const portfolioName   = searchParams.get("portfolio_name") || "";

  // ─── Filter state ─────────────────────────────────────────────
  // `days` == null when the user has entered a custom start/end range;
  // otherwise it's one of the WINDOW_OPTIONS presets (7 / 14 / 30).
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState("");  // "YYYY-MM-DD"
  const [endDate,   setEndDate]   = useState("");
  const [siteIds, setSiteIds] = useState([]);       // string[]  — empty = All sites
  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(false);

  useEffect(() => {
    api.get("/portal/sites").then((r) => setSites(r.data || [])).catch(() => setSites([]));
    api.get("/agency/portal-settings")
      .then((r) => setCommentsEnabled(!!(r.data?.comments || {})[kind]))
      .catch(() => setCommentsEnabled(false));
  }, [kind]);

  // Reset filters when switching between report kinds so a narrow filter
  // from one tab doesn't silently blank out another.
  useEffect(() => {
    setSiteIds([]);
    setDays(30);
    setStartDate("");
    setEndDate("");
  }, [kind]);

  // Apply portfolio drill-down once sites have loaded — pre-select the
  // site IDs belonging to the requested portfolio. "unassigned" targets
  // sites with no portfolio_id.
  useEffect(() => {
    if (!portfolioFilter || sites.length === 0) return;
    const matches = sites.filter((s) => {
      if (portfolioFilter === "unassigned") return !s.portfolio_id;
      return s.portfolio_id === portfolioFilter;
    }).map((s) => s.id);
    if (matches.length > 0) setSiteIds(matches);
  }, [portfolioFilter, sites]);

  const clearPortfolioFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("portfolio");
    next.delete("portfolio_name");
    setSearchParams(next, { replace: true });
    setSiteIds([]);
  };

  // Choose params: custom range wins when both endpoints are set, else preset days.
  const buildQueryParams = useCallback(() => {
    const p = {};
    if (siteIds.length > 0) p.site_ids = siteIds.join(",");
    if (startDate && endDate) {
      // Send full-day inclusive range as ISO strings
      p.start = new Date(startDate + "T00:00:00").toISOString();
      p.end   = new Date(endDate   + "T23:59:59").toISOString();
    } else if (days != null) {
      p.days = days;
    }
    return p;
  }, [siteIds, startDate, endDate, days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(cfg.endpoint, { params: buildQueryParams() });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(errorMessage(err));
      setRows([]);
    } finally { setLoading(false); }
  }, [cfg.endpoint, buildQueryParams]);

  useEffect(() => { load(); }, [load]);

  const onDownload = async () => {
    setDownloading(true);
    try {
      const p = buildQueryParams();
      const params = new URLSearchParams();
      Object.entries(p).forEach(([k, v]) => params.set(k, String(v)));
      const res = await fetch(`${API_BASE}/portal/reports/${cfg.pdfPath}.pdf?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cfg.pdfPath}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally { setDownloading(false); }
  };

  const siteMultiOptions = useMemo(() => (
    sites.map((s) => ({ value: s.id, label: s.name, sub: s.address }))
  ), [sites]);

  const hasCustomRange = Boolean(startDate && endDate);
  const clearCustomRange = () => { setStartDate(""); setEndDate(""); };
  const onPresetClick = (d) => {
    setDays(d);
    clearCustomRange();
  };

  const CardComp = CARDS[kind];

  return (
    <div data-testid={`portal-reports-page-${kind}`}>
      <PageHeader
        title={cfg.title}
        subtitle={cfg.subtitle}
        action={
          <GhostButton onClick={onDownload} disabled={downloading || rows.length === 0} data-testid={`portal-download-${kind}`}>
            <Download className="size-3.5 mr-1" /> {downloading ? "Preparing…" : "Download PDF"}
          </GhostButton>
        }
      />

      <div className="p-8 space-y-4">
        {/* Portfolio drill-down pill */}
        {portfolioFilter && (
          <div
            className="inline-flex items-center gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] px-3 py-1.5"
            data-testid="portal-portfolio-filter-pill"
          >
            <Layers className="size-3.5" />
            <span className="text-[11px] uppercase tracking-widest font-mono">Portfolio</span>
            <span className="font-display uppercase tracking-tight font-bold text-sm">
              {portfolioName || (portfolioFilter === "unassigned" ? "Unassigned" : portfolioFilter)}
            </span>
            <button
              type="button"
              onClick={clearPortfolioFilter}
              data-testid="portal-portfolio-filter-clear"
              title="Clear portfolio filter"
              className="ml-1 hover:text-white transition-colors"
            >
              <XIcon className="size-3.5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3" data-testid={`portal-report-filters-${kind}`}>
          <div className="min-w-[240px]">
            <div className="label-mute mb-1">Sites</div>
            <MultiSelectDropdown
              testId={`portal-site-filter-${kind}`}
              label="All sites"
              options={siteMultiOptions}
              selected={siteIds}
              onChange={setSiteIds}
              triggerClassName="min-w-[240px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Window</div>
            <div className="inline-flex items-center gap-2">
              {WINDOW_OPTIONS.map((opt) => {
                const active = !hasCustomRange && days === opt.days;
                return (
                  <button
                    key={opt.days}
                    data-testid={`portal-window-${opt.days}d`}
                    onClick={() => onPresetClick(opt.days)}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${
                      active
                        ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
                        : "border-[#27272A] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="label-mute mb-1">Custom range</div>
            <div className="inline-flex items-center gap-2">
              <input
                type="date"
                data-testid={`portal-start-date-${kind}`}
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-zinc-200 text-xs px-2 py-1.5 outline-none focus:border-[#FF3B30] font-mono"
              />
              <span className="text-zinc-500 text-xs">→</span>
              <input
                type="date"
                data-testid={`portal-end-date-${kind}`}
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-zinc-200 text-xs px-2 py-1.5 outline-none focus:border-[#FF3B30] font-mono"
              />
              {hasCustomRange && (
                <button
                  type="button"
                  onClick={clearCustomRange}
                  data-testid={`portal-clear-range-${kind}`}
                  title="Clear custom range"
                  className="p-1 text-zinc-500 hover:text-[#FF3B30]"
                >
                  <XIcon className="size-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="ml-auto text-xs text-zinc-500 font-mono">
            {loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "record" : "records"}`}
          </div>
        </div>

        {/* Cards */}
        <div className="space-y-3" data-testid={`portal-report-list-${kind}`}>
          {loading ? (
            <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">{cfg.empty}</div>
          ) : (
            rows.map((row) => (
              <CardComp
                key={row.id}
                row={row}
                onDetails={setDetailRow}
                onViewPhotos={(r) => setViewingPhotos({ items: r.photo_attachments, title: r.title || r.site_name })}
              />
            ))
          )}
        </div>
      </div>

      <PortalReportDetail kind={kind} row={detailRow} onClose={() => setDetailRow(null)} commentsEnabled={commentsEnabled} />
      {viewingPhotos && (
        <PhotoViewer
          items={viewingPhotos.items}
          title={viewingPhotos.title}
          onClose={() => setViewingPhotos(null)}
        />
      )}
    </div>
  );
}
